import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { PLOTS } from "../config/plots";
import { DWH_QUERIES } from "../config/dwhQueries";
import { useData } from "../data/DataContext";
import { useDwhData } from "../data/DwhDataContext";
import { getMeasureTags, getMeasureOwner } from "../config/plotGroups";

interface SideNavProps {
  isOpen: boolean;
  onToggle: () => void;
  selectedTag: string | null;
  selectedOwner: string | null;
}

const DWH_CHART_ITEMS = DWH_QUERIES.flatMap(q => [
  ...q.columns
    .filter(col => col.chartType != null)
    .map(col => ({
      id: `dwh-${q.id}-${col.key}`,
      label: `${q.label}: ${col.label}`,
    })),
  ...(q.stackedAreaCharts ?? []).map(chart => ({
    id: `dwh-${q.id}-${chart.id}`,
    label: chart.label,
  })),
]);

function HomeIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function AudienceIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function BudgetIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h14v4" />
      <path d="M4 6v12c0 1.1.9 2 2 2h14v-4" />
      <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
    </svg>
  );
}

export default function SideNav({ isOpen, onToggle, selectedTag, selectedOwner }: SideNavProps) {
  const [activeChartId, setActiveChartId] = useState<string | null>(null);
  const location = useLocation();
  const isDashboard = location.pathname === "/";
  const { isLoading } = useData();
  const { isLoading: dwhIsLoading } = useDwhData();
  const visibleCharts = useRef<Set<string>>(new Set());
  const navItemRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const filteredPlots = useMemo(() => {
    if (!selectedTag && !selectedOwner) return PLOTS;
    return PLOTS.filter(plot => {
      if (selectedTag && !getMeasureTags(plot.measureId).includes(selectedTag)) return false;
      if (selectedOwner && getMeasureOwner(plot.measureId) !== selectedOwner) return false;
      return true;
    });
  }, [selectedTag, selectedOwner]);

  const allLoading = isLoading || dwhIsLoading;

  // Auto-scroll nav to keep active item visible
  useEffect(() => {
    if (activeChartId && isOpen) {
      const activeButton = navItemRefs.current.get(activeChartId);
      activeButton?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [activeChartId, isOpen]);

  // Sync active chart to URL hash (debounced)
  useEffect(() => {
    if (!activeChartId) return;
    const timer = setTimeout(() => {
      const newHash = `#chart-${activeChartId}`;
      if (window.location.hash !== newHash) {
        window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${newHash}`);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [activeChartId]);

  useEffect(() => {
    // Wait until data is loaded and charts are rendered
    if (allLoading) return;

    let observer: IntersectionObserver | null = null;

    const updateActiveChart = () => {
      // Pick the visible chart whose top is closest to just below the sticky header.
      // Works for both scroll directions — the "current" chart is always the one
      // nearest to the header edge, whether approached from above or below.
      const headerBottom = 80;
      let bestId: string | null = null;
      let bestDist = Infinity;

      for (const id of visibleCharts.current) {
        const el = document.getElementById(`chart-${id}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.bottom < 0) continue; // fully above viewport
          const dist = Math.abs(rect.top - headerBottom);
          if (dist < bestDist) {
            bestDist = dist;
            bestId = id;
          }
        }
      }

      if (bestId) {
        setActiveChartId(bestId);
      }
    };

    // Small delay to ensure DOM is updated after data loads
    const timeoutId = setTimeout(() => {
      const allIds = [...PLOTS.map(plot => plot.id), ...DWH_CHART_ITEMS.map(item => item.id)];
      const chartElements = allIds
        .map(id => document.getElementById(`chart-${id}`))
        .filter((el): el is HTMLElement => el !== null);

      if (chartElements.length === 0) return;

      observer = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            const id = entry.target.id.replace("chart-", "");
            if (entry.isIntersecting) {
              visibleCharts.current.add(id);
            } else {
              visibleCharts.current.delete(id);
            }
          }
          updateActiveChart();
        },
        {
          root: null,
          rootMargin: "0px 0px -50% 0px", // Top half of viewport
          threshold: 0,
        },
      );

      for (const el of chartElements) {
        observer.observe(el);
      }

      // Set initial active chart from hash or viewport (scrolling handled by index.html)
      const hash = window.location.hash;
      const hashTarget = hash ? document.getElementById(hash.slice(1)) : null;
      if (hashTarget) {
        const id = hashTarget.id.replace("chart-", "");
        visibleCharts.current.add(id);
      } else {
        for (const el of chartElements) {
          const rect = el.getBoundingClientRect();
          if (rect.top < window.innerHeight * 0.5 && rect.bottom > 0) {
            visibleCharts.current.add(el.id.replace("chart-", ""));
          }
        }
      }
      updateActiveChart();
    }, 100);

    const chartsSet = visibleCharts.current;
    return () => {
      clearTimeout(timeoutId);
      observer?.disconnect();
      chartsSet.clear();
    };
  }, [allLoading]);

  const scrollToChart = useCallback((plotId: string, direction?: "up" | "down") => {
    const element = document.getElementById(`chart-${plotId}`);
    if (!element) return;
    const header = document.querySelector(".sticky-header") as HTMLElement | null;
    const headerH = header?.offsetHeight ?? 0;
    // When scrolling up the header will appear; when scrolling down it will hide.
    // If direction is unknown (click), check current state.
    const willShowHeader =
      direction === "up"
        ? true
        : direction === "down"
          ? false
          : header
            ? !header.classList.contains("sticky-header--hidden")
            : false;
    const offset = willShowHeader ? headerH + 8 : 8;
    const top = element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
  }, []);

  // Ordered list of visible chart IDs matching DOM order (DWH first, then WPR)
  const orderedIds = useMemo(() => {
    const dwh = DWH_CHART_ITEMS.map(item => item.id);
    const wpr = filteredPlots.map(plot => plot.id);
    return [...dwh, ...wpr];
  }, [filteredPlots]);

  // J/K keyboard navigation — use a ref so rapid keypresses don't depend on
  // async state updates from the IntersectionObserver.
  const keyTargetIdx = useRef<number>(-1);
  const lastKeyNav = useRef<number>(0);
  useEffect(() => {
    // Sync ref with observer-driven active chart, but not while keyboard
    // scroll is still in flight (smooth scroll takes ~500ms).
    if (Date.now() - lastKeyNav.current < 2000) return;
    const idx = activeChartId ? orderedIds.indexOf(activeChartId) : -1;
    if (idx !== -1) keyTargetIdx.current = idx;
  }, [activeChartId, orderedIds]);

  useEffect(() => {
    if (!isDashboard) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key !== "j" && e.key !== "k") return;

      lastKeyNav.current = Date.now();
      const step = e.key === "j" ? 1 : -1;
      let nextIdx = keyTargetIdx.current;
      // Scan forward/backward to find the next chart that exists in the DOM
      for (let i = 0; i < orderedIds.length; i++) {
        nextIdx += step;
        if (nextIdx < 0 || nextIdx >= orderedIds.length) {
          nextIdx = keyTargetIdx.current; // stay put at boundary
          break;
        }
        if (document.getElementById(`chart-${orderedIds[nextIdx]}`)) break;
      }
      keyTargetIdx.current = nextIdx;
      const nextId = orderedIds[nextIdx];
      if (nextId) scrollToChart(nextId, e.key === "j" ? "down" : "up");
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isDashboard, orderedIds, scrollToChart]);

  return (
    <nav className={`side-nav ${isOpen ? "side-nav--open" : ""}`}>
      <button
        type="button"
        className="side-nav__toggle"
        onClick={onToggle}
        aria-label={isOpen ? "Close navigation" : "Open navigation"}
      >
        {isOpen ? "✕" : "☰"}
      </button>
      <div className="side-nav__entity-links">
        <NavLink
          to="/"
          end
          title="Dashboard"
          className={({ isActive }) => `side-nav__entity-link${isActive ? " side-nav__entity-link--active" : ""}`}
        >
          <HomeIcon />
          <span>Dashboard</span>
        </NavLink>
        <NavLink
          to="/audiences"
          title="Audiences"
          className={({ isActive }) => `side-nav__entity-link${isActive ? " side-nav__entity-link--active" : ""}`}
        >
          <AudienceIcon />
          <span>Audiences</span>
        </NavLink>
        <NavLink
          to="/budgets"
          title="Budgets"
          className={({ isActive }) => `side-nav__entity-link${isActive ? " side-nav__entity-link--active" : ""}`}
        >
          <BudgetIcon />
          <span>Budgets</span>
        </NavLink>
      </div>
      {isDashboard && (
        <div className="side-nav__header">
          <h3>Charts</h3>
        </div>
      )}
      <ul className="side-nav__list">
        {isDashboard && DWH_CHART_ITEMS.length > 0 && (
          <>
            <li className="side-nav__section-label">DWH Metrics</li>
            {DWH_CHART_ITEMS.map(item => (
              <li key={item.id}>
                <button
                  type="button"
                  ref={el => {
                    if (el) navItemRefs.current.set(item.id, el);
                  }}
                  className={`side-nav__item ${activeChartId === item.id ? "side-nav__item--active" : ""}`}
                  onClick={() => scrollToChart(item.id)}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </>
        )}
        {isDashboard && <li className="side-nav__section-label">WPR Metrics</li>}
        {isDashboard &&
          filteredPlots.map(plot => (
            <li key={plot.id}>
              <button
                type="button"
                ref={el => {
                  if (el) navItemRefs.current.set(plot.id, el);
                }}
                className={`side-nav__item ${activeChartId === plot.id ? "side-nav__item--active" : ""}`}
                onClick={() => scrollToChart(plot.id)}
              >
                {plot.label}
              </button>
            </li>
          ))}
      </ul>
    </nav>
  );
}
